import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Profile } from '@/types/profile';

const PROFILE_QUERY_KEY = 'profile';

export function profileQueryKey(userId: string | undefined) {
  return [PROFILE_QUERY_KEY, userId];
}

/**
 * Centralized profile hook. Fetches the current user's profile once via
 * TanStack Query and serves all consumers from cache.
 */
export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<Profile | null>({
    queryKey: profileQueryKey(user?.id),
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as Profile | null;
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000,   // 2 minutes
    gcTime: 10 * 60 * 1000,     // 10 minutes
    refetchOnWindowFocus: false,
  });

  const invalidate = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY] });
  }, [queryClient]);

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    invalidate,
  };
}
